/**
 * JavaScript bridge for DOM-related utility commands triggered via Elm ports.
 *
 * This module supports scroll locking/unlocking and reacting to DOM readiness events.
 * Elm triggers commands using `domExtraPort_` with a tagged type string.
 */

import { AppWithPorts, ElmTaggedType, PortFromElm } from 'DTSLib/MPArchitectureFoundations/types'

/**
 * Resolves when the DOM is ready for interaction (DOMContentLoaded or already loaded).
 *
 * @returns Promise resolved with the DOMContentLoaded event or immediately if already ready.
 */
export async function onDOMReady(): Promise<Event | void> {
  return new Promise((resolve) => {
    const isDomReady = document.readyState === 'interactive' || document.readyState === 'complete'
    if (isDomReady) resolve()
    else document.addEventListener('DOMContentLoaded', (evt) => resolve(evt))
  })
}

/**
 * Resolves when the entire page (DOM + assets) is fully loaded.
 *
 * @returns Promise resolved with the load event or immediately if already complete.
 */
export async function onPageFullyLoaded(): Promise<Event | void> {
  return new Promise((resolve) => {
    const isDomLoaded = document.readyState === 'complete'
    if (isDomLoaded) resolve()
    else window.addEventListener('load', (evt) => resolve(evt))
  })
}

let scrollPosition = 0

/**
 * Prevents scrolling of the page and stores the current scroll position.
 */
export function lockPageScroll(): void {
  scrollPosition = window.scrollY
  document.documentElement.classList.add('scroll-locked')
}

/**
 * Restores the page scroll to the position before `lockPageScroll` and re-enables scrolling.
 */
export function unlockPageScroll(): void {
  document.documentElement.classList.remove('scroll-locked')
  window.scrollTo(0, scrollPosition)
}

/**
 * Structure of messages sent from Elm to trigger DOM actions.
 */
interface DomExtraPortValue extends ElmTaggedType {
  type_: 'lock_page_scroll' | 'unlock_page_scroll'
}

/**
 * Binds the Elm app to the DOM extra port handler.
 *
 * @param app - An Elm app instance with a `domExtraPort_` subscription.
 */
export function mount(app: AppWithPorts<DomExtraPortInterface>): void {
  app.ports.domExtraPort_.subscribe(domExtraPort_)
}

/**
 * Dispatches DOM utility actions triggered from Elm via the `domExtraPort_`.
 *
 * @param value - An object indicating the type of DOM action to perform.
 */
function domExtraPort_(value: DomExtraPortValue): void {
  switch (value.type_) {
    case 'lock_page_scroll':
      lockPageScroll()
      break
    case 'unlock_page_scroll':
      unlockPageScroll()
      break
  }
}

/**
 * Interface defining the DOM extra port required by the Elm app.
 */
export interface DomExtraPortInterface {
  domExtraPort_: PortFromElm<DomExtraPortValue>
}
